import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  keyframes,
  TextField,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { AutoAwesome } from "@mui/icons-material";
import { useState } from "react";
import { toast } from "react-toastify";
// Animation keyframes
const bounceIn = keyframes`
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    `;
const shake = keyframes`
      0% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      50% { transform: translateX(5px); }
      75% { transform: translateX(-5px); }
      100% { transform: translateX(0); }
    `;
const fadeIn = keyframes`
      0% { opacity: 0; transform: translateY(-10px); }
      100% { opacity: 1; transform: translateY(0); }
    `;
export const CommonInputDialog = ({ dialog, onClose }) => {
  const [inputText, setInputText] = useState("");
  // Get color based on type
  const getColor = () => {
    switch (dialog.type) {
      case "success":
        return "success.main";
      case "delete":
        return "error.main";
      case "fail":
        return "error.main";
      case "simple":
        if (dialog.subtype === "delete") return "warning.main";
        return "primary.main";
      default:
        return "primary.main";
    }
  };

  // Get icon based on type
  const getIcon = () => {
    const baseStyle = {
      fontSize: 24,
      marginRight: "16px",
      color: getColor(),
    };

    switch (dialog.type) {
      case "success":
        return (
          <CheckCircleOutlineIcon
            sx={{
              ...baseStyle,
              animation: `${bounceIn} 0.6s ease-out`,
            }}
          />
        );
      case "delete":
        return (
          <DeleteIcon
            sx={{
              ...baseStyle,
              animation: `${bounceIn} 0.6s ease-out`,
            }}
          />
        );
      case "fail":
        return (
          <ErrorOutlineIcon
            sx={{
              ...baseStyle,
              animation: `${shake} 0.5s ease-in-out`,
            }}
          />
        );
      case "simple":
        if (dialog.subtype === "delete") {
          return <WarningAmberIcon sx={baseStyle} />;
        }
        return <InfoOutlinedIcon sx={baseStyle} />;
      default:
        return <AutoAwesome sx={baseStyle} />;
    }
  };

  // Handle dialog close
  const handleClose = (event, reason) => {
    if (reason !== "backdropClick") {
      onClose();
      setInputText("");
    }
  };
  const onConfrm = () => {
    if (dialog.onConfirm) {
      if (!inputText) {
        toast.error("Please enter input text!");
        return;
      }
      dialog.onConfirm(inputText);
      setInputText("");
    }
  };
  return (
    <Dialog
      open={dialog.isInputOpen}
      onClose={handleClose}
      disa
      disableEscapeKeyDown={dialog.disableEscapeKeyDown}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: "350px",
          animation: `${fadeIn} 0.3s ease-out`,
        },
      }}
    >
      <DialogTitle sx={{ color: getColor() }}>
        {getIcon()}
        {dialog.title}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={dialog.inputLabel || "Enter message"}
          placeholder="Please type in your message"
          multiline
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          rows={4}
          margin="normal"
          size="small"
          variant="outlined"
        />
      </DialogContent>
      <DialogActions sx={{ pt: 1 }}>
        <Button autoFocus onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={onConfrm}
          variant={dialog.subtype === "delete" ? "contained" : "text"}
          autoFocus
          aria-label={dialog.buttonLabel || "Confirm dialog action"}
          sx={{
            color: dialog.subtype === "delete" ? "white" : getColor(),
            backgroundColor:
              dialog.subtype === "delete" ? getColor() : "transparent",

            borderRadius: 2,
            "&:hover": {
              backgroundColor:
                dialog.subtype === "delete" ? getColor() : `${getColor()}10`,
              opacity: 0.9,
            },
          }}
        >
          {dialog.buttonText || "OK"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
